import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { CenteredSpinner } from '~/commonComponents';
import { ReferralProgramRebuild } from '~/constants/ff.posthog';
import {
  extractReferralCodeFromUrl,
  referralService,
  setPendingReferralCode,
} from '~/domain/Referral';
import { useFeatureFlagState } from '~/hooks/useFeatureFlagState';
import { useUserSelector } from '~/hooks/useUserSelector';

export interface IReferralLinkRedirectProps {
  /** Where a signed-out visitor lands once the code is stored — the sign-up form. */
  signUpPath: string;
  /**
   * Where everyone else lands: the flag is off, the URL carries no valid code,
   * or an account already exists. The same place the `*` route sends them.
   */
  fallbackPath: string;
}

/**
 * The landing for a v2 referral link (GOTU-7432), the portal counterpart of
 * mobile's `openReferralV2` deep-link handler.
 *
 * The portal's `/` and `*` routes `<Navigate>` with a bare path, which drops
 * the query string, and `useReferralHandler`'s capture at the app root only
 * survives that by effect ordering. A dedicated route element is deterministic:
 * the URL is read here, before any redirect, and the redirect is issued last.
 *
 * With the rebuild flag on:
 * - **signed out** — the code is stored for the sign-up field (GOTU-7317),
 *   then the visitor is sent to sign-up. Store first, redirect last, so a
 *   navigation problem can never lose the code.
 * - **signed in** — nothing is stored: a code left in this browser would
 *   prefill a later sign-up with a self-referral. They land on the fallback
 *   carrying `showReferralNotEligible`, which the dashboard renders as the
 *   Not Eligible dialog (GOTU-7434). Not the Account Exists dialog: that one
 *   tells the office to log in, which they already have.
 *
 * Either way the claim is recorded (GOTU-7244) — measurement, never awaited,
 * and the service swallows its own failures — because this is the first portal
 * load carrying the code, which is what makes the claim table a denominator.
 *
 * With the flag off, or without a well-formed code, the link is inert and the
 * visitor lands exactly where the `*` route would have sent them, with nothing
 * written to storage. The flag is awaited (`useFeatureFlagState`) so a cold
 * load is never decided on a transient "off".
 */
export const ReferralLinkRedirect = ({ signUpPath, fallbackPath }: IReferralLinkRedirectProps) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const user = useUserSelector();
  const flagState = useFeatureFlagState(ReferralProgramRebuild);
  // One decision per arrival: a re-render after `navigate` (or a user selector
  // settling) must not run the branch twice.
  const hasDecided = useRef(false);

  useEffect(() => {
    if (flagState === 'loading' || hasDecided.current) return;
    hasDecided.current = true;

    const code = extractReferralCodeFromUrl(`${pathname}${search}`);

    if (flagState !== 'enabled' || !code) {
      navigate(fallbackPath, { replace: true });
      return;
    }

    void referralService.recordReferralClaim(code);

    if (user) {
      // GOTU-7434: a signed-in office has nothing to sign up for, so the
      // referral cannot apply. The landing page reads this off the navigation
      // state and shows the dialog, the way `NotAllowedGuard` does — mobile
      // uses a redux flag because its deep-link router sits outside React;
      // here the redirect already owns the decision.
      navigate(fallbackPath, { replace: true, state: { showReferralNotEligible: true } });
      return;
    }

    setPendingReferralCode(code);
    navigate(signUpPath, { replace: true });
  }, [flagState, pathname, search, user, navigate, signUpPath, fallbackPath]);

  return <CenteredSpinner />;
};
